import React               from 'react';
import View                from '@widesk-ui/components/View';
import { SelectOutlined }  from '@ant-design/icons';
import { RightOutlined }   from '@ant-design/icons';
import PagedCollection     from '@widesk-core/models/PagedCollection';
import FieldTypeModel      from '@models/map/FieldTypeModel';
import { message }         from '@widesk-ui/hooks/useMessage';
import nextFreeLabel       from '@/tools/nextFreeLabel';
import FieldModel          from '@models/map/FieldModel';
import SelectField         from '@/selects/services/map/SelectField';
import useDesignTokens     from '@/hooks/useDesignTokens';
import { NATURES }         from '@/forms/services/map/fieldNatures';
import ProjectModel        from '@models/map/ProjectModel';
import FunctionalityGate   from '@/components/Functionality';
import { getIdFromUrn }    from '@widesk-core/tools/identifier';

/**
 * Bouton d'ajout d'un champ à un type, en deux temps : la nature d'abord, le nommage ensuite.
 *
 * Le second temps réutilise `FieldForm` tel quel, avec une seule `fieldTypeReference` — il masque
 * alors son sélecteur de type et résout l'URN correspondante lui-même. Il n'y a donc rien à
 * réimplémenter de la création d'un champ, seulement à choisir pour l'utilisateur.
 *
 * L'écran ne CRÉE rien lui-même : il résout l'URN du `FieldType` correspondant à la nature choisie,
 * puis passe le champ à créer à `onCreate`. C'est l'appelant qui écrit, parce que la création et le
 * rattachement au type doivent partir ensemble — un champ créé sans rattachement serait orphelin
 * (voir `PlaceTypeForm.addNewField`). `onCreate` rend le modèle créé, ou `undefined` si l'écriture a
 * échoué ; le modèle entier et non son URN, pour que l'appelant l'affiche sans rechargement.
 */
/**
 * Écran d'ajout d'un champ : il remplace le contenu de la sidebar (voir `PlaceTypeForm`), au lieu de
 * se déplier sous le bouton. Un panneau étroit ne laisse pas la place à six natures décrites, et
 * l'utilisateur est de toute façon dans une tâche à part entière — il doit la terminer ou revenir.
 */
export default function FieldNaturePicker(props: {
	projectUrn: Urn;
	onCreate: (attributes: { label: string; fieldType: Urn }) => Promise<FieldModel | undefined>;
	onReuse: (field: FieldModel) => void;
	onDone: () => void;
	usedUrns: Urn[];
	/** Libellés déjà pris dans le projet, pour que le nom généré soit libre. */
	takenLabels: string[];
}) {
	const tokens = useDesignTokens();

	// Le sélecteur de réutilisation se déplie SOUS la liste des natures, il ne la remplace pas. En
	// écran séparé, la flèche de retour de la sidebar sautait un niveau — elle ramenait à la fiche du
	// type et non aux natures — et il n'y a qu'une flèche pour deux profondeurs.
	const [showReuse, setShowReuse] = React.useState(false);

	const project = React.useMemo(() => new ProjectModel({ id: getIdFromUrn(props.projectUrn) }), [props.projectUrn]);

	return (
		<View gap={2}>
			{/* En premier, avant la création : réutiliser évite de multiplier dans le projet des champs
			    qui disent la même chose. Proposer d'abord d'en créer un revenait à suggérer le doublon. */}
			<View
				row
				gap="sm"
				p="xs"
				br={6}
				pointer
				bg={showReuse ? tokens.surface.subtle : undefined}
				hover={{ bg: tokens.surface.subtle }}
				onClick={() => setShowReuse(v => !v)}
			>
				<View
					w={26}
					h={26}
					br={7}
					center
					color={tokens.color.primary}
					bg={tokens.fill.subtle}
					style={{ flexShrink: 0 }}
				>
					<SelectOutlined />
				</View>

				<View flex gap={3}>
					<View bold size="small">Réutiliser {FieldModel.localize('a', 'l')} existant</View>
					<View size="small" color={tokens.text.muted}>
						Déjà défini pour un autre type. Le modifier le met à jour partout.
					</View>
				</View>

				{/* Icône plutôt qu'un caractère : les glyphes ▸/▾ ne s'alignent pas sur la ligne de base du
				    texte et ne suivent pas la graisse des autres icônes de l'écran. Rotation de 90° à
				    l'ouverture, la convention d'un bloc dépliable. */}
				<RightOutlined
					rotate={showReuse ? 90 : 0}
					style={{ fontSize: 11, color: tokens.text.muted, transition: 'transform .2s ease' }}
				/>
			</View>

			{showReuse && (
				<View px="xs" pt="xs">
					<SelectField
						projectUrn={props.projectUrn}
						placeholder={FieldModel.localize()}
						disabledOption={m => props.usedUrns.includes(m.urn)}
						onModelChange={model => {
							if (!model) return;

							props.onReuse(model);
							props.onDone();
						}}
					/>
				</View>
			)}

			<View h={1} bg={tokens.border.default} marginV="sm" />

			<View bold size="small" color={tokens.text.secondary} pb="xs">Ou créer un nouveau champ</View>

			<FunctionalityGate reference="place_field_limit" context={project} fullWidth>
				<View gap={2}>
					{NATURES.map(nature => (
						<View
							key={nature.reference}
							row
							gap="sm"
							p="xs"
							br={6}
							pointer
							hover={{ bg: tokens.surface.subtle }}
							// Création directe, sans passer par un formulaire : le nom est généré et se corrige
							// ensuite en place dans la liste, où il est déjà modifiable. Demander un titre avant
							// d'avoir vu la ligne apparaître était une étape de trop pour une valeur qu'on
							// ajustera de toute façon. Même geste que `addAndEditTheme` pour les thèmes.
							onClick={async () => {
								props.onDone();

								let fieldType: Urn | undefined;

								try {
									// La nature est choisie, mais l'API attend l'URN du `FieldType` correspondant.
									const types = new PagedCollection(FieldTypeModel);
									await types.setRequiredFilter('reference', nature.reference).setItemsPerPage(1).list();

									fieldType = types.first()?.urn;

									if (!fieldType) throw new Error(`Nature "${nature.label}" introuvable côté API.`);
								} catch (err) {
									return message.exception(err);
								}

								const label = nextFreeLabel(nature.label, props.takenLabels);

								// Volontairement hors du `try` : l'appelant signale déjà ses erreurs d'écriture et
								// porte l'indicateur d'enregistrement. Rien n'est annoncé ici non plus en cas de
								// succès — c'est lui qui confirme la création, avec une annulation à portée de clic.
								await props.onCreate({ label, fieldType });
							}}
						>
							<View
								w={26}
								h={26}
								br={7}
								center
								color={tokens.color.primary}
								bg={tokens.fill.subtle}
								style={{ flexShrink: 0 }}
							>
								{nature.icon}
							</View>

							<View flex gap={3}>
								<View bold size="small">{nature.label}</View>
								<View size="small" color={tokens.text.muted}>{nature.hint}</View>
							</View>
						</View>
					))}
				</View>
			</FunctionalityGate>
		</View>
	);
}
